import { useEffect, useRef, type RefObject } from 'react';
import { useNavigate } from 'react-router';
import type { AppNotification } from '../../hooks/useNotifications';
import { formatRelativeTime } from '../../utils/dateUtils';

interface NotificationsPopoverProps {
    open: boolean;
    onClose: () => void;
    notifications: AppNotification[];
    onMarkAllRead: () => Promise<void>;
    onOpenNotification: (notification: AppNotification) => Promise<string>;
    /**
     * The boundary the outside-click check treats as "inside" — must wrap both the trigger (the
     * bell) and this popover, the same way legacy NotificationIcon's single `dropdownRef` wraps
     * its toggle button and its dropdown. Without this, a click on the bell itself bubbles a
     * `mousedown` that this component sees as "outside" (since the popover's own DOM node doesn't
     * contain the bell) and closes on it — then the bell's own click handler re-opens it a beat
     * later, so the bell can never close an open popover.
     */
    boundaryRef: RefObject<HTMLElement | null>;
}

const titleId = 'notifications-popover-title';

/**
 * The prototype's notification popover. Presentation only — all fetching, polling, and mark-read
 * logic lives in useNotifications(); RedesignHeader owns the one shared hook instance (for its
 * badge count) and passes the pieces this needs down as props, rather than this component calling
 * the hook a second time and running a second, independent poll.
 *
 * Unmounted entirely while closed (`open` false renders null) rather than kept mounted and hidden
 * like MoreSheet — there is no slide transition to preserve here, so nothing needs `inert`: an
 * unmounted popover is already out of the tab order and the accessibility tree.
 */
export default function NotificationsPopover({ open, onClose, notifications, onMarkAllRead, onOpenNotification, boundaryRef }: NotificationsPopoverProps) {
    const dialogRef = useRef<HTMLDivElement>(null);
    const previouslyFocused = useRef<HTMLElement | null>(null);
    const navigate = useNavigate();

    useEffect(() => {
        if (!open) return;

        // Same focus contract as MoreSheet: capture whatever had focus (the bell, in practice)
        // before moving focus into the popover, and hand it back on close.
        previouslyFocused.current = document.activeElement as HTMLElement | null;
        dialogRef.current?.focus();

        return () => {
            previouslyFocused.current?.focus();
        };
    }, [open]);

    useEffect(() => {
        if (!open) return;

        function handleKeyDown(e: KeyboardEvent) {
            if (e.key === 'Escape') onClose();
        }

        function handleClickOutside(e: MouseEvent) {
            // Checked against boundaryRef (bell + popover), not dialogRef (popover only) — see
            // the prop's doc comment for why that distinction is the whole fix.
            if (boundaryRef.current && !boundaryRef.current.contains(e.target as Node)) {
                onClose();
            }
        }

        document.addEventListener('keydown', handleKeyDown);
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('keydown', handleKeyDown);
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [open, onClose, boundaryRef]);

    if (!open) return null;

    const handleItemClick = async (notification: AppNotification) => {
        const route = await onOpenNotification(notification);
        navigate(route);
        onClose();
    };

    return (
        <div
            ref={dialogRef}
            role="dialog"
            aria-labelledby={titleId}
            tabIndex={-1}
            data-testid="notifications-popover"
            className="fixed left-2 right-2 top-16 w-auto md:left-auto md:right-6 md:w-[360px] bg-surface border border-border rounded-2xl shadow-[0_16px_40px_rgba(28,33,28,.25)] overflow-hidden z-60"
        >
            <div className="flex justify-between items-center py-3.5 px-[18px] border-b border-border-inner">
                <h2 id={titleId} className="text-sm font-bold text-ink">Notifications</h2>
                {notifications.length > 0 && (
                    <button
                        type="button"
                        onClick={onMarkAllRead}
                        className="text-xs font-semibold text-leaf"
                    >
                        Mark all as read
                    </button>
                )}
            </div>

            {notifications.length === 0 ? (
                <p className="py-8 px-[18px] text-center text-[13px] text-ink-muted">You're all caught up</p>
            ) : (
                <div>
                    {notifications.map((notification) => (
                        <button
                            key={notification.id}
                            type="button"
                            onClick={() => handleItemClick(notification)}
                            className={`flex w-full items-start gap-2.5 text-left py-[13px] px-[18px] border-b border-border-inner last:border-b-0 cursor-pointer ${
                                !notification.isRead ? 'bg-leaf-tint' : ''
                            }`}
                        >
                            {/* A fixed-width slot either way, so read and unread titles share one
                                left edge. The dot is decoration; the sr-only word is what a screen
                                reader hears, first, as part of the button's name. */}
                            <span className="flex-none w-2 pt-[5px]">
                                {!notification.isRead && (
                                    <span
                                        data-testid="notification-unread-dot"
                                        aria-hidden="true"
                                        className="block size-2 rounded-full bg-leaf"
                                    />
                                )}
                            </span>
                            <span className="min-w-0 flex-1">
                                {!notification.isRead && <span className="sr-only">Unread: </span>}
                                <span className="block truncate text-[13px] font-bold text-ink" title={notification.title}>{notification.title}</span>
                                {notification.message && (
                                    <span className="block text-xs text-ink-muted mt-0.5 line-clamp-2">{notification.message}</span>
                                )}
                                <span className="block text-[11px] text-ink-faint mt-1">
                                    {formatRelativeTime(notification.createdAt)}
                                </span>
                            </span>
                        </button>
                    ))}
                </div>
            )}

            <div className="py-[11px] px-[18px] border-t border-border-inner text-center">
                <button
                    type="button"
                    onClick={onClose}
                    className="text-[12.5px] font-semibold text-ink-muted"
                >
                    Close
                </button>
            </div>
        </div>
    );
}
